import {
  isSourceVisible, type AiVisibility, type AiVisibilityKey,
} from '../constants/aiVisibility';

// AI assistant context — the structured snapshot of the user's state that gets
// woven into the system prompt so the assistant feels like *theirs*. Fed by the
// surface that hosts the assistant (it needs `useUserStats`, a React hook), then
// formatted to a compact prompt block at send time by `formatAIContext`.

export interface AIDomainContext {
  id: string;
  name: string;
  level: number;
  rank?: string;
}

export interface AIFoundationContext {
  purpose?: string;
  vision?: string;
  /** The user's per-domain mission lines (only domains that have one). */
  missions: Array<{ domain: string; mission: string }>;
  /** Top talents from the Talent Discovery assessment, if taken. */
  topTalents?: string[];
}

export interface AIContextData {
  userLevel: number;
  domains: AIDomainContext[];
  foundation: AIFoundationContext;
  activeTasks: number;
  activeHabits: number;
  activeProjects: number;
  recentActivity: string[];
}

/**
 * Render the context to a compact markdown block for the system prompt.
 * Kept terse on purpose — it rides in every message, so it pays tokens each turn.
 *
 * ⚠️⚠️ **A SWITCHED-OFF SOURCE IS NOT SENT, NOT REDACTED.** The block is built
 * here, in the browser, so refusing a source means the bytes never leave the
 * device — there is no "[hidden]" placeholder and nothing for a prompt to talk
 * around. That is the whole reason the context half of `aiVisibility` needs no
 * server. ⚠️ The READ TOOLS are the other half and cannot be enforced here; see
 * `constants/aiVisibility.ts`.
 *
 * ⚠️ `visibility` is optional and `undefined` shows everything — every existing
 * caller keeps today's behaviour without being touched.
 */
export function formatAIContext(ctx: AIContextData, visibility?: AiVisibility): string {
  const lines: string[] = [];
  const show = (key: AiVisibilityKey) => isSourceVisible(visibility, key);

  if (show('identity')) {
    lines.push(`Overall level: ${ctx.userLevel}`);

    if (ctx.domains.length > 0) {
      const domainStr = ctx.domains
        .map((d) => `${d.name} L${d.level}${d.rank ? ` (${d.rank})` : ''}`)
        .join(', ');
      lines.push(`Domains: ${domainStr}`);
    }
  }

  const f = ctx.foundation;
  if (show('direction')) {
    if (f.purpose?.trim()) lines.push(`Purpose: ${f.purpose.trim()}`);
    if (f.vision?.trim()) lines.push(`Vision: ${f.vision.trim()}`);
    if (f.missions.length > 0) {
      lines.push('Objectives:');
      for (const m of f.missions) {
        if (m.mission?.trim()) lines.push(`  - ${m.domain}: ${m.mission.trim()}`);
      }
    }
  }
  if (show('talents') && f.topTalents && f.topTalents.length > 0) {
    lines.push(`Top talents: ${f.topTalents.join(', ')}`);
  }

  if (show('openItems')) {
    lines.push(
      `Open items: ${ctx.activeTasks} tasks, ${ctx.activeHabits} habits, ${ctx.activeProjects} projects`
    );
  }

  if (show('recentActivity') && ctx.recentActivity.length > 0) {
    lines.push(`Recent: ${ctx.recentActivity.join('; ')}`);
  }

  return lines.join('\n');
}
